import { useState, useEffect } from 'react';
import { AlertTriangle, Info, AlertCircle, Check, RefreshCw } from 'lucide-react';
import { getInsights, acknowledgeInsight } from '../lib/api';
import type { Insight } from '../lib/types';

const severityConfig = {
  info: { icon: Info, color: 'text-blue-400', bg: 'bg-blue-500/10' },
  warning: { icon: AlertTriangle, color: 'text-yellow-400', bg: 'bg-yellow-500/10' },
  critical: { icon: AlertCircle, color: 'text-red-400', bg: 'bg-red-500/10' },
};

export function InsightsFeed() {
  const [insights, setInsights] = useState<Insight[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAcknowledged, setShowAcknowledged] = useState(false);

  const loadInsights = async () => {
    setIsLoading(true);
    setError(null);

    const response = await getInsights(showAcknowledged ? undefined : false);

    if (response.success && response.data) {
      setInsights(response.data.insights);
    } else {
      setError(response.error || 'Failed to load insights');
    }

    setIsLoading(false);
  };

  useEffect(() => {
    loadInsights();
  }, [showAcknowledged]);

  const handleAcknowledge = async (insightId: string) => {
    const response = await acknowledgeInsight(insightId);

    if (response.success) {
      setInsights((prev) =>
        prev.map((i) => (i.id === insightId ? { ...i, acknowledged: true } : i))
      );
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Insights</h2>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-gray-400">
            <input
              type="checkbox"
              checked={showAcknowledged}
              onChange={(e) => setShowAcknowledged(e.target.checked)}
              className="rounded border-gray-600 bg-gray-800 text-brain-500 focus:ring-brain-500"
            />
            Show acknowledged
          </label>
          <button
            onClick={loadInsights}
            disabled={isLoading}
            className="p-2 text-gray-400 hover:text-gray-200 transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {error && (
        <div className="card border-red-900 bg-red-950/50">
          <p className="text-red-400">{error}</p>
        </div>
      )}

      {!isLoading && insights.length === 0 && (
        <div className="card text-center py-8">
          <Info className="w-8 h-8 text-gray-600 mx-auto mb-2" />
          <p className="text-gray-500">No unacknowledged insights</p>
        </div>
      )}

      <div className="space-y-3">
        {insights.map((insight) => {
          const config = severityConfig[insight.severity];
          const Icon = config.icon;

          return (
            <div
              key={insight.id}
              className={`card ${config.bg} border-gray-800 ${
                insight.acknowledged ? 'opacity-60' : ''
              }`}
            >
              <div className="flex items-start gap-3">
                <Icon className={`w-5 h-5 ${config.color} mt-0.5`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="font-medium">{insight.title}</p>
                      <p className="text-sm text-gray-400 mt-1">{insight.description}</p>
                    </div>
                    {!insight.acknowledged && (
                      <button
                        onClick={() => handleAcknowledge(insight.id)}
                        className="p-1.5 text-gray-400 hover:text-green-400 transition-colors"
                        title="Acknowledge"
                      >
                        <Check className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                    <span>{insight.source}</span>
                    <span>{insight.category}</span>
                    <span>{new Date(insight.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
